import React, { useCallback } from 'react'
import { FLAVOUR_TITLES, DEFAULT_FLAVOUR } from 'constants/flavours'
import cx from 'classnames'
import Icon from 'components/shared/Icon'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import TealToolTip from 'components/TealToolTip'
import { useSelector } from 'react-redux'
import selectFlavour from 'redux/selectors/selectFlavour'
import selectBottleLink from 'redux/selectors/selectBottleLink'

const FlavourTabs = ({ isProxy = false, isSearch = false, flavours }) => {
  const bottleLink = useSelector(selectBottleLink)
  const flavour = useSelector(selectFlavour) || DEFAULT_FLAVOUR

  return (
    <div className='layout-width-constraint'>
      <nav className='pt5 pb2'>
        <div className='flex justify-center flavour-tabs pt6 pb3 relative'>
          {!isSearch && (
            <div className='absolute f5 left0 bottom3 flex items-center'>
              <Hidden smDown>
                <Icon name='icon-qa-search' className='w7 mr4' />
                <span className={cx(`color-${flavour}`)}>Q&A</span>
              </Hidden>
            </div>
          )}
          <ul className='flex list-reset pa0 ma0 w-80' data-help-item='flavours' data-help-position='bottom'>
            {flavours.map(_flavour => (
              <Tab
                key={_flavour}
                active={!isProxy && _flavour === flavour}
                {...{ bottleLink, isProxy, _flavour }}
              />
            ))}
          </ul>
        </div>
      </nav>
    </div>
  )
}

export default FlavourTabs

const Tab = ({ _flavour: flavour, bottleLink, active, isProxy }) => {
  const href = active || isProxy ? null : bottleLink(flavour)
  const className = cx(
    'pa4 pa3-t pa2-m transition-all db no-underline bg-qa-tab relative',
    { pointer: !isProxy },
    { wait: isProxy },
    {
      'selected white default-cursor': active,
      [flavour]: true,
      'cadet-blue': !active
    }
  )

  return (
    <LinkWrapper {...{ flavour }}>
      <a
        {...{ href, className }}
        tabIndex='-1'
        data-event-category='Flavour'
        data-event-action='Select'
        data-event-label={flavour}
      >
        <DynamicFlavourTabLabel {...{ flavour, active }} />
      </a>
    </LinkWrapper>
  )
}

export const DynamicFlavourTabLabel = ({ flavour, active, title, isAnnotationPage }) => (
  <>
    <Hidden smDown>
      <FlavourTab desktop {...{ flavour, active, title, isAnnotationPage }} />
    </Hidden>
    <Hidden mdUp>
      <FlavourTab {...{ flavour, active, title, isAnnotationPage }} />
    </Hidden>
  </>
)

export const LinkWrapper = ({ flavour, title, isAnnotationPage = false, children }) => (
  <li className={cx({ 'w-25': !isAnnotationPage }, { 'w-20': isAnnotationPage })}>
    <Paper className='mh1' classes={{ root: 'br0 pa0' }}>
      <Hidden smUp>
        <TealToolTip title={title || flavour} placement='top'>
          {children}
        </TealToolTip>
      </Hidden>
      <Hidden xsDown>
        {children}
      </Hidden>
    </Paper>
  </li>
)

const FlavourTab = ({ desktop = false, flavour, active, title, isAnnotationPage }) => (
  <Grid container justify={desktop ? 'space-around' : 'center'}>
    <Grid item xs={desktop ? false : 12}>
      {desktop ? <FlavourTabTitle {...{ flavour, active, title, isAnnotationPage }} /> : <FlavourTabIcon {...{ flavour, active }} />}
    </Grid>
    <Grid item xs={desktop ? false : 12}>
      {desktop ? <FlavourTabIcon {...{ flavour, active }} /> : <FlavourTabTitle {...{ flavour, active, title, isAnnotationPage }} />}
    </Grid>
  </Grid>
)

const FlavourTabTitle = ({ flavour, title, active, isAnnotationPage }) => (
  <Hidden xsDown>
    <span
      className={cx(
        { 'f5 f6-t f7-m': isAnnotationPage },
        { 'f5 f6-t f7-m': !isAnnotationPage },
        'fw6 tracked-cosy db relative tc no-select',
        { white: active }
      )}
    >
      {title || FLAVOUR_TITLES[flavour]}
    </span>
  </Hidden>
)

const FlavourTabIcon = ({ flavour, active }) => {
  const getIconName = useCallback(() => {
    switch (flavour) {
      case 'archive':
        return 'icon-archive'
      case 'legislation':
        return 'icon-legislation'
      default:
        return `icon-bottle-${flavour}`
    }
  }, [flavour])

  return (
    <span className={cx(`color-${flavour} db relative flex justify-center`, { active })}>
      <Icon name={getIconName()} className={cx('vm w7')} />
    </span>
  )
}
